import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import { JobsStore } from "../main/accountant/jobs-and-tasks/jobs.store";
import { EmptyStateComponent } from "src/app/design-system/components/empty-state/empty-state.component";
import { DashboardService } from "../dashboard.service";
import { Data } from "@angular/router";
import { PinnedService } from "../main/accountant/pinned-panel/pinned.service";
import {
  PinnedItem,
  PinnedItemType,
  PinnedModels,
  PinnedStatus,
} from "../main/accountant/pinned-panel/pinned-models";
import { SharedDataService } from "../shared-data.service";
import { LazyLoadEvent, SharedModule } from "primeng/api";
import { DashboardStore } from "../dashboard.store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs";
import { MomentModule } from "ngx-moment";
import { PinnedCardComponent } from "../main/accountant/pinned-panel/pinned-card/pinned-card.component";
import { TableModule } from "primeng/table";
import { FormsModule } from "@angular/forms";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { NgIf, NgFor, NgClass } from "@angular/common";
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  UserPermissions,
} from "src/app/services/authorization/authorization.models";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-pinned-items-dashboard",
  templateUrl: "./pinned-items-dashboard.component.html",
  styleUrls: [
    "./pinned-items-dashboard.component.scss",
    "../main/accountant/jobs-and-tasks/time-tracking/time-tracking-dashboard/time-tracking-dashboard.component.scss",
  ],
  imports: [
    NgIf,
    BsDropdownModule,
    NgFor,
    DatePickerModule,
    FormsModule,
    TableModule,
    SharedModule,
    PinnedCardComponent,
    NgClass,
    MomentModule,
    EmptyStateComponent,
  ],
})
export class PinnedItemsDashboardComponent implements OnInit, OnDestroy {
  readonly ROLE_MANAGER = ROLE_MANAGER;
  readonly ROLE_ADMIN = ROLE_ADMIN;
  readonly JSON = JSON;
  date;
  pinnedItems: PinnedItem[];
  data: Data;
  PinnedStatus = PinnedStatus;
  PinnedItemType = PinnedItemType;
  PinnedModels = PinnedModels;
  dashboardName = "pinnedDashboard";

  initialFilters = {
    [this.dashboardName]: {
      personFilter: { label: "Everyone", value: "everyone" },
      timeFrameFilter: { label: "All", value: null },
      assignedToFilter: { label: "Me", value: "me" },
      pinnedStatusFilter: { label: "To-do", value: PinnedStatus.Pinned },
      rows: 25,
    },
  };

  cols: any[];
  totalRecords: number = 0;
  loading: boolean = false;
  lastLazyEvent: LazyLoadEvent;
  userPermissions: UserPermissions;

  @ViewChild("calendar") calendar: DatePicker;

  destroyRef = inject(DestroyRef);

  constructor(
    private pinnedService: PinnedService,
    private sharedDataService: SharedDataService,
    public jobsStore: JobsStore,
    private dashboardStore: DashboardStore,
    public dashboardService: DashboardService,
    private localStorage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.userPermissions = this.localStorage.get("userPermissions");
    this.data = this.sharedDataService.data;
    if (!this.dashboardService.filters()[this.dashboardName]) {
      this.dashboardService.filters.update((prevState = {}) => ({
        ...prevState,
        ...this.initialFilters,
      }));
    }
    this.pinnedService.unpinItemSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uuid) => {
        this.pinnedItems = this.pinnedItems.filter(
          (item) => item.uuid !== uuid,
        );
      });
    this.cols = [
      { field: "assigned_to?.full_name", header: "Pinned For" },
      { field: "pinned_by.full_name", header: "Pinned By" },
      { field: "pinned_dt", header: "Pinned Date" },
      { field: "model_name", header: "Type" },
      { field: "model_name", header: "Pinned Item" },
      { field: "pinned_status", header: "Pinned Status" },
    ];
  }

  ngOnDestroy(): void {}

  async retrievePinneditems(event = null): Promise<void> {
    this.loading = true;
    if (event) this.lastLazyEvent = event;
    const {
      [this.dashboardName]: {
        personFilter: { value: personFilter },
        timeFrameFilter: { value: timeFrameFilter },
        assignedToFilter: { value: assignedToFilter },
        pinnedStatusFilter: { value: pinnedStatusFilter },
      },
    } = this.dashboardService.filters();

    const { pinned_items, total_count } =
      await this.pinnedService.retrieveAllPinnedItems(
        personFilter,
        assignedToFilter,
        timeFrameFilter,
        pinnedStatusFilter,
        this.lastLazyEvent.first,
        this.lastLazyEvent.rows,
      );
    this.pinnedItems = pinned_items;
    this.totalRecords = total_count;
    this.loading = false;
  }

  getClientName(clientUuid: string): string {
    const { name } = this.dashboardStore.clients.find(
      ({ uuid }) => uuid === clientUuid,
    );
    return name;
  }

  getClientCompanyId(clientUuid: string): number {
    const { id } = this.dashboardStore.clients.find(
      ({ uuid }) => uuid === clientUuid,
    );
    return id;
  }

  changeAssignedToFilter(change: string | { id: number; name: string }) {
    let label;
    let value;
    if (typeof change === "string") {
      value = change;
      label = change.charAt(0).toUpperCase() + change.slice(1);
    } else {
      value = change.id;
      label = change.name;
    }
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        assignedToFilter: { label, value },
      },
    }));
    this.retrievePinneditems();
  }

  changePersonFilter(change: string | { id: number; name: string }): void {
    let label;
    let value = change;
    if (change == "me") {
      label = "Me";
    } else if (change == "everyone") {
      label = "Everyone";
    }

    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        personFilter: { label, value },
      },
    }));
    this.retrievePinneditems();
  }

  onDateChange(): boolean {
    if (this.date && this.date[0] && this.date[1]) {
      this.changeTimeFrameFilter("custom_date");
      return true;
    }
    return false;
  }

  changeTimeFrameFilter(change: string[] | string): void {
    let value = change;
    let label;
    if (value == "this_week") {
      label = "This week";
    }

    if (value == "today") {
      label = "Today";
    }

    if (value == "last_week") {
      label = "Last week";
    }

    if (value == "last_two_weeks") {
      label = "Last two weeks";
    }

    if (value == "last_four_weeks") {
      label = "Last four weeks";
    }

    if (value == "last_month") {
      label = "Last month";
    }

    if (value == "custom_date") {
      value = this.date;
      label = `${this.date[0].replace(
        /\./g,
        "-",
      )}  to  ${this.date[1].replace(/\./g, "-")}`;
    }
    if (!value) {
      label = "All";
    }
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        timeFrameFilter: { label, value },
      },
    }));
    this.date = "";
    this.retrievePinneditems();
  }

  changePinnedStatusFilter(change: number): void {
    let value = change;
    let label =
      change === PinnedStatus.Pinned
        ? "To-do"
        : change === PinnedStatus["Pinned Complete"]
          ? "Completed"
          : "All";
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        pinnedStatusFilter: { label, value },
      },
    }));
    this.retrievePinneditems();
  }

  isPinnedItemNote(pinnedItem: PinnedItem) {
    return pinnedItem.model_name === PinnedModels.Note;
  }

  onPageChange(event) {
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        rows: event.rows,
      },
    }));
  }

  exportPdf(): void {}

  exportExcel(): void {}

  exportCsv(): void {}
}
